
import { clearCuttingRules } from "@/utils/cuttingRules";
import { getImgSizeByUrl } from "@/views/AIEditV3/utils/utils";
/**
 * 根据url获取图片宽高
 * @param url 图片url
 * @param callback 回调函数 参数为宽高
 * @returns void
 */
export function getImageWidthAndHeight(url: string, callback: (width: number, height: number) => any, noCache = false) {
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.src = url
  // 如果图片被缓存，则直接返回缓存数据
  if (img.complete && noCache === false) {
    callback(img.width, img.height)
    return
  }
  // 完全加载完毕的事件
  img.onload = function () {
    // 图片通过onload加载完毕后，width和height属性可用
    callback(img.width, img.height)
    img.onload = null
    return
  }
}

export function getAsyncImageWidthAndHeight(url: string, noCache = false): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous' // 允许跨域图片
    img.src = url
    // 图片已缓存且无需强制刷新
    if (img.complete && !noCache) {
      resolve({ width: img.width, height: img.height })
      return
    }
    img.onload = () => {
      //二次校验防止因元数据缺失导致的无效尺寸
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
        })
      }
       else {
        reject('')
      }
    }
    // 加载失败时的处理
    img.onerror = (err) => {
      reject('')
    }
  })
}
/**
 * 根据url获取图片来源
 * @param url 图片url
 * @returns string 图片来源 LG: 灵感，MS: 主站，UPLOAD：用户上传，AI：ai生成图片，FRONT: 前端改图，UNKNOWN：未知来源, COLLECT: 收藏图片 , OFFICIAL: 官方图例 
 * ALICK :复制指令or画同款
 */
export type ImageSource = 'LG' | 'MS' | 'UPLOAD' | 'AI' | 'FRONT' | 'COLLECT' | 'OFFICIAL' | 'ALICK' | 'UNKNOWN' 
export const getImageSource = (url: string): ImageSource => {
  if (url.indexOf('lgpic.3d66.com') > -1 && url.indexOf('!lg-ai') === -1) {
    // 灵感
    return 'LG'
  } else if (url.indexOf('respic.3d66.com') > -1) {
    // 主站
    return 'MS'
  } else if (
    (url.indexOf('imgo.3d66.com') > -1 && url.indexOf('ai/reference_image') > -1) ||
    url.indexOf('3d66.oss-cn-hangzhou.aliyuncs.com') > -1 ||
    url.indexOf('3d66-imgo.oss-cn-hangzhou.aliyuncs.com') > -1
  ) {
    // 用户上传 imgo.3d66.com : master；3d66.oss-cn-hangzhou.aliyuncs.com : test；3d66-imgo.oss-cn-hangzhou.aliyuncs.com：旧数据
    return 'UPLOAD'
  } else if (url.indexOf('aiimg.3d66.com') > -1) {
    // ai生成图片
    return 'AI'
  } else if (url.indexOf('imgo.3d66.com') > -1 && url.indexOf('ai/redraw/output') > -1) {
    return 'FRONT'
  }
  return 'UNKNOWN'
}
type ImageSourceEnum = 1 | 2 | 3 | 4 ; //1：用户上传，2：画同款，3：官方图例，4：收藏夹
export const getImageSourceEnum = (source: ImageSource): ImageSourceEnum => {
  switch (source) {
    case 'UPLOAD':
      return 1
    case 'ALICK':
      return 2
    case 'OFFICIAL':
      return 3
    case 'COLLECT':
      return 4
    default:
      return 1
  }

}

/**
 * 缩略图
 * 给图片加上高度160的裁图规则
 * 域名 aiimg.3d66.com → 用 !ai_r_h_160
 * 其他域名 → 用 ?x-oss-process=image/resize,h_160,m_lfit
 * @param {string} url - 图片 URL
 * @returns {string} 返回一个裁图规则后的图片 URL
 */
export const getAddH160RulesImageUrl = (imageUrl: string) => {
  imageUrl = clearCuttingRules(imageUrl);
  try {
    const { hostname } = new URL(imageUrl);
    const domain = hostname.toLowerCase();
    if (domain === 'aiimg.3d66.com') {
      return `${imageUrl}!ai_r_h_160`;
    } else {
      return `${imageUrl}?x-oss-process=image/resize,h_160`;
    }
  } catch (e) {
    return imageUrl;
  }
};
/**
 * 压缩图片
 * 给aiimg.3d66.com 地址的图片加上1200像素的裁图规则
 * @param imageUrl 图片地址
 * @returns {string} 添加裁图规则后的图片地址
 */
export const getAddUp1200RulesImageUrl = (imageUrl: string) => {
  imageUrl = clearCuttingRules(imageUrl);
  try {
    const { hostname } = new URL(imageUrl);
    const domain = hostname.toLowerCase();
    if (domain === 'aiimg.3d66.com') {
      return `${imageUrl}!ai_upload_1200`;
    } else {
      return imageUrl;
    }
  } catch (e) {
    return imageUrl;
  }
};
/**
 * 生成图片查看大图or下载裁图规则
 * @param img_url 图片地址
 * @param image_size 图片尺寸
 */
export function getAddProRulesImageUrl(img_url: string, image_size: { width: number; height: number }) {
  const { width, height } = image_size
  const maxDimension = Math.max(width, height)
  let rulesImgUrl =  clearCuttingRules(img_url)
  // 应用裁图规则
  if (maxDimension > 0 && maxDimension < 1500) {
    rulesImgUrl += '!ai_draw_download_1200'
  } else if (maxDimension < 2500) {
    rulesImgUrl += '!ai_draw_download_2000'
  } else if (maxDimension < 3500) {
    rulesImgUrl += '!ai_draw_download_3000'
  } else if (maxDimension < 4500) {
    rulesImgUrl += '!ai_draw_download_4000'
  } else {
    rulesImgUrl += '!ai_draw_download_5000'
  }
  return rulesImgUrl
}
/**
 * 异步获取生成图片查看大图or下载裁图规则
 * @param img_url 图片地址
 * @param image_size 图片尺寸 非必传
 */
export async function getAddProRulesImageUrlAsync(
  img_url: string, 
  image_size?: { width: number; height: number }
): Promise<string> {
  let rulesImgUrl = clearCuttingRules(img_url);
  
  // 处理尺寸获取逻辑
  let size: { width: number; height: number };
  if (image_size?.width && image_size?.height) {
    size = image_size;
  } else {
    // 异步获取图片尺寸
    const imgSize = await getImgSizeByUrl(rulesImgUrl);
    size = {
      width: imgSize.width,
      height: imgSize.height
    };
  }

  // 确保尺寸有效
  if (size.width <= 0 || size.height <= 0) {
    throw new Error('Invalid image dimensions');
  }

  const maxDimension = Math.max(size.width, size.height);
  const rulesSuffix = 
    maxDimension < 1500 ? '!ai_draw_download_1200' :
    maxDimension < 2500 ? '!ai_draw_download_2000' :
    maxDimension < 3500 ? '!ai_draw_download_3000' :
    maxDimension < 4500 ? '!ai_draw_download_4000' :
    '!ai_draw_download_5000';

  return rulesImgUrl + rulesSuffix;
}

/**
 * 画布预览图裁图规则 - 最大1200像素
 * @param img_url 图片地址
 * @param image_size 图片尺寸
 * 具体规则说明：https://alidocs.dingtalk.com/i/nodes/XPwkYGxZV3RyXLXgH409XAvvWAgozOKL?utm_scene=team_space&iframeQuery=anchorId%3Duu_ma24okf1mplbg0k4utm
 */
export function getAddPreviewRulesImageUrl(img_url: string, image_size: { width: number; height: number }) {
  const { width, height } = image_size
  const maxDim = Math.max(width, height)
  let imgUrl = clearCuttingRules(img_url)
  if (getImageSource(imgUrl) === 'UPLOAD' && maxDim >= 1200) {
    return img_url + '!ai_upload_1200'
    // return imgUrl + '?x-oss-process=image/resize,w_1200' // 将图片宽度固定为1200像素，高度自适应进行缩小
  }
  if (getImageSource(imgUrl) === 'LG') {
    return imgUrl + '!lg-ai'
  }
  if (getImageSource(imgUrl) === 'AI') {
    return imgUrl + '!ai_draw_look_1200'
  }
  return imgUrl
}

/**
 * 缩放图片至1200像素
 * @param originalSize 图片源size
 * @returns 缩放后的size
 */
export const getZoomTo1200Size = (originalSize: { width: number; height: number }):{ width: number; height: number }  => {
  // 目标宽度
  const targetWidth = 1200
  // 等比计算新高度
  const newHeight = (targetWidth / originalSize.width) * originalSize.height
  return { width: targetWidth, height: Math.round(newHeight) }
}


/**
 * 上传图片处理-将图片尺寸限制为1200px
 * 大于1200px时进行裁剪(添加裁图规则) 小于1200px时进行将等比缩放为w1200
 * @param originalImgUrl 图片源url
 * @param originalSize 图片源size
 * @returns imgUrl 添加1200裁图规则的图片  size 添加裁图规则后的图片尺寸
 */
export const uploadImageAsyncProcess = async (originalImgUrl: string, originalSize: any): Promise<{ imgUrl: string; size: any }> => {
  const imgUrl = getAddPreviewRulesImageUrl(originalImgUrl, originalSize)
  // const size: any = await getAsyncImageWidthAndHeight(imgUrl)
  const size: any = await getImgSizeByUrl(imgUrl)

  if(size.width > 1200){ // 添加裁图规则后尺寸
    return { imgUrl, size }
  }else{
    const zoom_size = getZoomTo1200Size(originalSize)
    return { imgUrl , size: zoom_size }
  }
}



/**
 * 缩略图
 * 图片宽度固定为 w 像素，高度自适应（x-oss-process 裁图规则）
 * @param url 图片 URL
 * @param w 目标宽度（像素）
 * @returns 添加裁图规则后的图片 URL
 */
export const getSmallImageUrl = (url: string, w: number) => {
  if (!url || /^(blob:|data:)/i.test(url) || !/^https?:\/\//i.test(url)) return url

  return clearCuttingRules(url) + `?x-oss-process=image/resize,limit_0,w_${w}`
}

/**
 * 固定尺寸 WebP 缩略图，适用于 cover 展示的卡片图片。
 */
export const getWebpThumbnailUrl = (url: string, width: number, height: number) => {
  if (!url || /^(blob:|data:)/i.test(url) || !/^https?:\/\//i.test(url)) return url

  return clearCuttingRules(url)
    + `?x-oss-process=image/resize,m_fill,w_${width},h_${height}/format,webp/quality,Q_80`
}



/**
 * 大图压缩规则
 * 当图片最长边大于 4000px 时，统一使用 OSS 裁图规则将最长边压缩至 3000px
 * @param img_url 图片地址
 * @param image_size 图片尺寸 { width, height }；不可用时内部通过 OSS `?x-oss-process=image/info` 接口兜底获取宽高
 * @returns 添加 `?x-oss-process=image/resize,l_3000` 裁图规则后的图片 URL；最长边 ≤ 4000px 或尺寸获取失败时原样返回
 */
export async function getOssResizeL3000ImageUrl(img_url: string, image_size?: { width: number; height: number }) {
  if (!img_url || /^(blob:|data:)/i.test(img_url) || !/^https?:\/\//i.test(img_url)) return img_url
  let maxDimension = Math.max(image_size?.width || 0, image_size?.height || 0)
  if (maxDimension <= 0) {
    // 尺寸兜底：调用 OSS image/info 接口获取原图宽高
    try {
      const size = await getImgSizeByUrl(img_url)
      maxDimension = Math.max(size.width || 0, size.height || 0)
    } catch {
      return img_url
    }
  }
  if (maxDimension <= 4000) return img_url
  return clearCuttingRules(img_url) + '?x-oss-process=image/resize,l_3000'
}
